import React, {useState, useEffect} from "react";
import {useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import {
  AuthenticationBasic,
  AuthenticationToken,
  AuthMethod,
  TopicDescription,
  TopicDescriptionPullTransfer,
} from "../../../logic/APITypes";
import {
  DropDown,
  NumberInput,
  TextInput,
  DropDownOption,
} from "@spg/component_library";
import {DefaultInputBehavior} from "../../../reusables/DefaultInputBehavior";
import {AuthenticationSubSection} from "./AuthenticationSubSection";

/**
 * Controls the transfer section when creating, viewing and editing topic descriptions with pull direction.
 */
function PullTransferSection(props: {
  authentication: AuthMethod;
  setAuthentication: (authentication: AuthMethod) => void;
  alternative: "create" | "view" | "edit";
  waitingForResponse: boolean;
  pullTransferInitTrigger: number;
  pullTransferNullTrigger: number;
  setPullInfo: (
    unmodifiedInputs: boolean,
    validInputs: boolean,
    strategy: TopicDescription["information_strategy"],
    transfer: TopicDescriptionPullTransfer,
  ) => void;
  initialTopic: Resolution<TopicDescription> | undefined;
}) {
  const intl = useIntl();
  // States for tracking form contents and potential errors
  const [name, setName] = useState("");
  const [secret, setSecret] = useState("");
  const [interval, setInterval] = useState("900");
  const [url, setUrl] = useState("");
  const strategyOptions: DropDownOption<
    TopicDescription["information_strategy"]
  >[] = [
    {
      value: "EMAIL",
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.strategy.EMAIL",
      }),
    },
    {
      value: "SMS",
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.strategy.SMS",
      }),
    },
  ];
  const [strategy, setStrategy] = useState<null | DropDownOption<
    TopicDescription["information_strategy"]
  >>(null);

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  /** Initializes form with values based on the initialTopic if available,
   * otherwise only info available from the overview table is filled in.
   * For the creation alternatives default values (mostly empty) are used. */
  const setInitialStates = () => {
    const transfer = initialTopic?.transfer as
      | TopicDescriptionPullTransfer
      | undefined;
    setUrl(transfer?.url || "");
    setInterval(transfer?.interval.toString() || "900");
    setStrategy(
      strategyOptions.find(
        (dfo) => dfo.value === initialTopic?.information_strategy,
      ) || null,
    );
    setName("");
    setSecret("");
    props.setAuthentication(transfer?.authentication?.method || "NULL");
  };

  useEffect(() => {
    if (props.pullTransferInitTrigger) setInitialStates();
  }, [props.pullTransferInitTrigger]);

  useEffect(() => {
    if (props.pullTransferNullTrigger) {
      setName("");
      setSecret("");
      setInterval("900");
      setUrl("");
      setStrategy(null);
      props.setAuthentication("NULL");
    }
  }, [props.pullTransferNullTrigger]);

  useEffect(() => {
    if (!strategy || initialTopic?.transfer.direction === "PUSH") return;
    let authentication:
      | AuthenticationBasic
      | AuthenticationToken
      | {method: AuthMethod};
    if (props.authentication === "BASIC" && name !== "" && secret !== "") {
      authentication = {
        username: name,
        password: secret,
        method: "BASIC",
      };
    } else if (
      (props.authentication === "TOKEN_HEADER" ||
        props.authentication === "TOKEN_QUERY") &&
      name !== "" &&
      secret !== ""
    ) {
      authentication = {
        token: secret,
        name: name,
        method: props.authentication,
      };
    } else {
      authentication = {
        method: props.authentication,
      };
    }
    const transfer: TopicDescriptionPullTransfer = {
      transfer_type: "REST",
      direction: "PULL",
      authentication: authentication,
      interval: parseInt(interval),
      url: url,
    };

    const unmodifiedInputs =
      initialTopic?.transfer.authentication?.method === props.authentication &&
      name === "" &&
      secret === "" &&
      parseInt(interval) === initialTopic?.transfer.interval &&
      url === initialTopic?.transfer.url &&
      strategy?.value === initialTopic?.information_strategy;

    const validInputs =
      ((name.length === 0 &&
        secret.length === 0 &&
        (props.authentication === "NULL" ||
          (props.alternative === "edit" &&
            initialTopic?.transfer.authentication?.method ===
              props.authentication))) ||
        (name.length > 0 && secret.length > 0)) &&
      url.length > 0 &&
      !isNaN(parseInt(interval)) &&
      10 <= parseInt(interval) &&
      parseInt(interval) <= 1000000 &&
      strategy !== null;

    props.setPullInfo(
      unmodifiedInputs,
      validInputs as boolean,
      strategy?.value || "EMAIL",
      transfer,
    );
  }, [
    name,
    secret,
    interval,
    url,
    strategy,
    props.authentication,
    props.alternative,
  ]);

  return (
    <>
      <div className="topicDescriptionCreationIntervalContainer">
        <DefaultInputBehavior
          component={NumberInput}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.interval",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationInterval"}
          value={interval}
          setValue={setInterval}
          minValue={10}
          maxValue={1000000}
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            initialTopic?.transfer.interval !== parseInt(interval)
          }
        />
      </div>
      <div className="topicDescriptionCreationSpecificsContainer">
        <DefaultInputBehavior
          component={TextInput}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.URL",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationURL"}
          value={url}
          setValue={setUrl}
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            initialTopic?.transfer.url !== url
          }
        />
      </div>
      <div className="topicDescriptionCreationStrategyContainer">
        <DefaultInputBehavior
          component={DropDown}
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.strategy",
          })}
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: "topicDescriptionCreation.inputLabel.strategy",
            })
          }
          idBase={"topicDescriptionCreationStrategy"}
          options={strategyOptions}
          selectedOption={strategy}
          setSelectedOption={(newStrategy) =>
            setStrategy(newStrategy as typeof strategy)
          }
          isRequired
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            (initialTopic &&
              strategy &&
              initialTopic?.information_strategy !== strategy.value) as boolean
          }
        />
      </div>
      <AuthenticationSubSection
        alternative={props.alternative}
        authentication={{
          get: props.authentication,
          set: props.setAuthentication,
        }}
        name={{get: name, set: setName}}
        secret={{get: secret, set: setSecret}}
        waitingForResponse={props.waitingForResponse}
        initialTopic={props.initialTopic}
      />
    </>
  );
}

export {PullTransferSection};
